import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { initialMarginCalculationsSlice } from './margin-calculations.slice';
import { environment } from 'src/environments/environment';
import { MarginCalculations } from '../../models/margin-calculations.model';
import { computed, inject, Signal } from '@angular/core';
import { AdditionalRatesStore } from '../additional-rates/additional-rates.store';
import { MarginExpensesStore } from '../margin-expenses/margin-expenses.store';
import { MarginSetupStore } from '../margin-setup/margin-setup.store';
import { MarginExpenseTypesAttributes } from '../../enums/margin-expense-types-attributes.enum';
import { MarginExpenseTypes } from '../../enums/margin-expense-types.enum';
import { MarginExpense } from '../../models/margin-expense.model';
import { TargetMargin } from '../../models/target-margin.model';
import { CommissionTier } from '../../models/commission-tier.model';
import { MalpracticeInsuranceRate } from 'src/app/recruiting/locums-margin-calculator/models/malpractice-insurance-rate.model';
import { AdditionalRate } from 'src/app/recruiting/locums-margin-calculator/models/additional-rate.model';
import { JobDetailsStore } from '../job-details/job-details.store';
import { deriveHoursByAdditionalRate } from 'src/app/recruiting/locums-margin-calculator/helpers/margin-calculations-hours.helper';
import { AdditionalRateTypes } from 'src/app/recruiting/locums-margin-calculator/enums/additional-rate-types.enum';

export const MarginCalculationsStore = signalStore(
    { providedIn: 'root' },

    withState(initialMarginCalculationsSlice),

    withComputed((store) => {
        // stores...
        const jobDetailsStore = inject(JobDetailsStore);
        const additionalRatesStore = inject(AdditionalRatesStore);
        const marginSetupStore = inject(MarginSetupStore);
        const marginExpensesStore = inject(MarginExpensesStore);

        // ---- Revenue ----
        const shiftRevenue = computed(
            () => marginSetupStore.totalHours() * marginSetupStore.billRate() + additionalRatesStore.totalRevenue()
        );

        const selectedMalpracticeInsuranceRateSignal = selectedMalpracticeInsuranceRate(
            store.malpracticeInsuranceRates,
            jobDetailsStore.professionId,
            jobDetailsStore.specialtyId
        );

        const targetMargin = selectedTargetMargin(store.targetMargins, jobDetailsStore.professionId);

        const billableExpenses = computed(() =>
            getBillableExpenses(marginExpensesStore.marginExpenses, marginExpensesStore.expenseTotalCost)
        );

        const totalRevenue = computed(
            () => shiftRevenue() * (1 - (store.contractFee() ?? 0) / 100) + billableExpenses()
        );

        // ---- Cost ----
        const shiftCost = computed(() =>
            getShiftCost(
                additionalRatesStore.additionalRates, // Signal<AdditionalRate[]>
                marginSetupStore.totalHours, // Signal<number>
                marginSetupStore.payRate, // Signal<number>
                additionalRatesStore.expense // Signal<(id)=>number|string...>
            )
        );

        const malpracticeInsurance = computed(() =>
            getMalpracticeInsuranceTotal(
                selectedMalpracticeInsuranceRateSignal, // Signal<number>
                additionalRatesStore.additionalRates, // Signal<AdditionalRate[]>
                marginSetupStore.totalHours // Signal<number>
            )
        );

        const accommodations = computed(() => getAccommodationsTotal(marginExpensesStore.expenseTotalCost));

        const otherExpenses = computed(() => getOtherExpensesTotal(marginExpensesStore.marginExpenses));

        const totalCost = computed(() => getTotalCost(shiftCost, malpracticeInsurance, accommodations, otherExpenses));

        // ---- Profit & Commission ----
        const grossProfit = computed(() => totalRevenue() - (totalCost() ?? 0));

        // these helpers already expect signals, so pass the signals themselves.
        const grossProfitMargin = getGrossProfitMargin(grossProfit, totalRevenue);

        const estimatedCommissionTiersPerHour = computed(
            () => (profitPercent: number) =>
                getEstimatedCommissionTiersPerHour(
                    additionalRatesStore.additionalRates,
                    marginSetupStore.totalHours,
                    grossProfit,
                    profitPercent
                )
        );

        const commissionTier = computed(() => getCommissionTier(store.commissionTiers, grossProfitMargin));

        const totalCommission = computed(() => getTotalCommission(grossProfitMargin, commissionTier, shiftRevenue));

        const estimatedCommissionPerHour = getEstimatedCommissionPerHour(
            additionalRatesStore.additionalRates,
            marginSetupStore.totalHours,
            totalCommission
        );

        return {
            // revenue
            malpracticeInsuranceRate: selectedMalpracticeInsuranceRateSignal,
            targetMargin,
            shiftRevenue,
            billableExpenses,
            totalRevenue,

            // cost
            shiftCost,
            malpracticeInsurance,
            accommodations,
            otherExpenses,
            totalCost,

            // profit & commissions
            grossProfit,
            grossProfitMargin,
            estimatedCommissionTiersPerHour,
            commissionTier,
            totalCommission,
            estimatedCommissionPerHour
        };
    }),

    withComputed((store) => ({
        snapshot: computed(() => ({
            grossProfit: store.grossProfit(),
            grossProfitMargin: store.grossProfitMargin(),
            commissionTier: store.commissionTier(),
            estimatedCommissionPerHour: store.estimatedCommissionPerHour(),
            estimatedCommissionPerShift: store.estimatedCommissionPerShift(),
            totalCommission: store.totalCommission(),
            targetMargin: store.targetMargin(),
            shiftRevenue: store.shiftRevenue(),
            billableExpenses: store.billableExpenses(),
            contractFee: store.contractFee(),
            totalRevenue: store.totalRevenue(),
            shiftCost: store.shiftCost(),
            malpracticeInsuranceRate: store.malpracticeInsuranceRate(),
            malpracticeInsurance: store.malpracticeInsurance(),
            accommodations: store.accommodations(),
            otherExpenses: store.otherExpenses(),
            totalCost: store.totalCost()
        }))
    })),

    withMethods((store) => ({
        updateMarginCalculationsFromData: (data: MarginCalculations) => {
            patchState(store, { ...data });
        },

        updateMarginCalculationsField: (data: Partial<MarginCalculations>) => {
            patchState(store, { ...data });
        },

        getState: () => store,

        getSnapshot: () => store.snapshot()
    })),

    environment.storeWithDevTools('locums-margin-calculator-margin-calculations-store')
);

/**
 * Finds the appropriate commission tier profit percentage based on gross profit margin.
 *
 * @param commissionTiers - Array of commission tiers
 * @param grossProfitMargin - The gross profit margin percentage to find a matching tier for
 * @returns The profit percentage from the matching tier, or 0 if no match is found
 */
function getCommissionTier(commissionTiers: Signal<CommissionTier[]>, grossProfitMargin: Signal<number>): number {
    return (
        commissionTiers()?.find(
            (rate) =>
                (rate.minGrossMarginPercent === null || grossProfitMargin() >= rate.minGrossMarginPercent) &&
                (rate.maxGrossMarginPercent === null || grossProfitMargin() <= rate.maxGrossMarginPercent)
        )?.profitPercent ?? 0
    );
}

/**
 * Computes the selected malpractice insurance rate based on the candidate's profession and expertise.
 *
 * The calculation logic follows this priority:
 * - If a profession and expertise are selected, finds the rate that matches both the profession and contains the specific expertise.
 * - If only a profession is selected, finds the general rate for that profession (without specific expertise requirements).
 * - Returns 0 if no profession is selected or no matching rates are found.
 *
 * @param {InstanceType<typeof this>} store - The margin calculations store containing malpractice insurance rates.
 * @param {InstanceType<typeof CandidateDetailsStore>} candidateDetailsStore - The candidate details store containing profession and expertise information.
 * @returns {Signal<number>} A computed signal that returns the selected malpractice insurance rate. Returns 0 if no applicable rate is found.
 */
function selectedMalpracticeInsuranceRate(
    malpracticeInsuranceRates: Signal<MalpracticeInsuranceRate[]>,
    professionId: Signal<number>,
    specialtyId: Signal<number>
) {
    return computed(() => {
        if (!malpracticeInsuranceRates() || !professionId()) {
            return 0;
        }

        const ratesPerSelectedProfession = malpracticeInsuranceRates().filter(
            (rate) => rate.professionId === professionId()
        );
        if (!ratesPerSelectedProfession.length) {
            return 0;
        }

        if (specialtyId()) {
            const rateWithExpertise = ratesPerSelectedProfession.find((rate) =>
                rate.expertises?.some((exp) => exp.id === specialtyId())
            );
            if (rateWithExpertise) {
                return rateWithExpertise.rate ?? 0;
            }
        }

        const professionOnlyRate = ratesPerSelectedProfession.find((rate) => !rate.expertises?.length);
        return professionOnlyRate?.rate ?? 0;
    });
}

function getBillableExpenses(
    marginExpenses: Signal<MarginExpense[]>,
    expenseTotalCost: Signal<(marginExpenseTypeId: MarginExpenseTypes) => string>
) {
    const expenseTypesBillable = marginExpenses()
        .filter(
            (e) =>
                e.marginExpenseAttributeId === MarginExpenseTypesAttributes.IsBillable &&
                e.value === 'true' &&
                !e.isDeleted
        )
        .map((e) => ({ expenseTypeId: e.marginExpenseTypeId, rowId: e.rowId }));

    let total = 0;
    expenseTypesBillable.forEach(({ expenseTypeId, rowId }) => {
        if (
            [MarginExpenseTypes.RENTAL_CAR, MarginExpenseTypes.HOUSING, MarginExpenseTypes.AIRFARE].includes(
                expenseTypeId
            )
        ) {
            total += Number(expenseTotalCost()(expenseTypeId));
        } else {
            const expense = marginExpenses().find(
                (e) =>
                    e.marginExpenseTypeId === expenseTypeId &&
                    e.marginExpenseAttributeId === MarginExpenseTypesAttributes.Amount &&
                    e.rowId === rowId
            );
            const amount = expense?.value ?? 0;
            total += Number(amount);
        }
    });
    return total;
}

function selectedTargetMargin(targetMargins: Signal<TargetMargin[]>, professionId: Signal<number | null | undefined>) {
    return computed(() => {
        return targetMargins()?.find((m) => m.professionId === professionId())?.targetMargin ?? null;
    });
}

function getTotalCommission(
    grossProfitMargin: Signal<number>,
    commissionTier: Signal<number>,
    shiftRevenue: Signal<number>
) {
    const totalCommission = (grossProfitMargin() / 100) * (commissionTier() / 100) * shiftRevenue();
    return Math.round(totalCommission * 100) / 100; // rounding to 2 decimal places
}

function getGrossProfitMargin(grossProfit: Signal<number>, totalRevenue: Signal<number>) {
    return computed<number>(() => {
        if (Number(totalRevenue() ?? 0) === 0) {
            return 0;
        }

        const grossProfitMargin = (grossProfit() * 100) / totalRevenue();
        return Math.round(grossProfitMargin * 100) / 100; // rounding to 2 decimal places
    });
}

function getEstimatedCommissionTiersPerHour(
    additionalRates: Signal<AdditionalRate[]>,
    marginSetupTotalHours: Signal<number>,
    grossProfit: Signal<number>,
    profitPercent: number
) {
    const totalExpectedHours = getTotalExpectedHours(additionalRates, marginSetupTotalHours);
    if (totalExpectedHours === 0) {
        return 0;
    }
    const estimatedCommissionPerHour = (grossProfit() * (profitPercent / 100)) / totalExpectedHours;
    return Math.round(estimatedCommissionPerHour * 100) / 100; // rounding to 2 decimal places
}

function getTotalExpectedHours(additionalRates: Signal<AdditionalRate[]>, totalHoursFromMarginSetup: Signal<number>) {
    const totalHoursFromAdditionalRates = additionalRates()
        .filter(
            (additionalRate) =>
                !additionalRate.isDeleted &&
                (additionalRate.marginCoverageTypeId === 2 ||
                    additionalRate.additionalRateTypeId === AdditionalRateTypes.CALL_BACK)
        )
        .reduce((sum, additionalRate) => sum + deriveHoursByAdditionalRate(additionalRate), 0);

    return totalHoursFromAdditionalRates + totalHoursFromMarginSetup();
}

function getMalpracticeInsuranceTotal(
    selectedMalpracticeInsuranceRate: Signal<number>,
    additionalRates: Signal<AdditionalRate[]>,
    totalHours: Signal<number>
): number {
    const rate = selectedMalpracticeInsuranceRate?.() ?? 0;
    const onsite = getTotalExpectedHours(additionalRates, totalHours);
    return rate * onsite;
}

function getAccommodationsTotal(expenseTotalCost: Signal<(typeId: MarginExpenseTypes) => string>): number {
    const expenseTotalCostFn = expenseTotalCost?.();

    if (!expenseTotalCostFn) {
        return 0;
    }

    const airfareTotalCost = Number(expenseTotalCostFn(MarginExpenseTypes.AIRFARE));
    const rentalCarTotalCost = Number(expenseTotalCostFn(MarginExpenseTypes.RENTAL_CAR));
    const housingTotalCost = Number(expenseTotalCostFn(MarginExpenseTypes.HOUSING));

    return airfareTotalCost + rentalCarTotalCost + housingTotalCost;
}

function getOtherExpensesTotal(marginExpenses: Signal<MarginExpense[]>): number {
    // exclude accommodations
    const excludedExpenseTypes = new Set<number>([
        MarginExpenseTypes.AIRFARE,
        MarginExpenseTypes.RENTAL_CAR,
        MarginExpenseTypes.HOUSING
    ]);

    // only include active, non-accommodation rows
    const marginExpenseRows = (marginExpenses?.() ?? []).filter(
        (marginExpense) => !marginExpense.isDeleted && !excludedExpenseTypes.has(marginExpense.marginExpenseTypeId)
    );

    // sum all Amount values for the included expense types
    let total = 0;
    for (const marginExpense of marginExpenseRows) {
        if (marginExpense.marginExpenseAttributeId === MarginExpenseTypesAttributes.Amount) {
            const n = Number(marginExpense.value ?? 0);
            if (Number.isFinite(n)) {
                total += n;
            }
        }
    }

    return total;
}

function getTotalCost(
    shiftCost: Signal<number>,
    malpracticeInsurance: Signal<number>,
    accommodations: Signal<number>,
    otherExpenses: Signal<number>
): number {
    return (
        (shiftCost?.() ?? 0) + (malpracticeInsurance?.() ?? 0) + (accommodations?.() ?? 0) + (otherExpenses?.() ?? 0)
    );
}

function getShiftCost(
    additionalRates: Signal<AdditionalRate[]>,
    totalHours: Signal<number>,
    payRate: Signal<number | null>,
    expenseFn: Signal<(selectedRate: AdditionalRate | undefined) => number>
): number {
    // base cost: core hours * base pay rate
    const marginSetupExpense = (totalHours?.() ?? 0) * (payRate?.() ?? 0);

    const additionalRatesExpenses = (additionalRates() ?? [])
        .filter((additionalRate) => !additionalRate.isDeleted)
        .reduce((sum, additionalRate) => {
            return sum + ((expenseFn?.() ?? (() => 0))(additionalRate) ?? 0);
        }, 0);

    return marginSetupExpense + additionalRatesExpenses;
}

function getEstimatedCommissionPerHour(
    additionalRates: Signal<AdditionalRate[]>,
    marginSetupTotalHours: Signal<number>,
    totalCommission: Signal<number>
) {
    return computed<number>(() => {
        const totalExpectedHours = getTotalExpectedHours(additionalRates, marginSetupTotalHours);

        if (totalExpectedHours === 0) {
            return 0;
        }

        const commission = totalCommission() / totalExpectedHours;
        return Math.round(commission * 100) / 100; // rounding to 2 decimal places
    });
}
